import { useCallback, useEffect, useState } from "react";
import { Award, Loader2, Save, Star, TrendingUp } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const ZONE_STYLE = {
  green: "bg-emerald-500/15 text-emerald-700 border-emerald-500/40",
  yellow: "bg-amber-500/15 text-amber-700 border-amber-500/40",
  red: "bg-rose-500/15 text-rose-700 border-rose-500/40",
  danger: "bg-red-900/20 text-red-800 border-red-900/40",
  no_data: "bg-muted text-muted-foreground border-border",
};

export const ScoreChip = ({ metric, testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
    ZONE_STYLE[metric.zone] || ZONE_STYLE.no_data}`} data-testid={testId}>
    {metric.label}: {metric.no_data ? "no data" : metric.display}
  </span>
);

const Card = ({ card }) => (
  <div className="surface space-y-2 p-4" data-testid={`score-card-${card.user_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <div className="text-sm font-semibold">
          {card.name}
          <span className="ml-2 text-xs text-muted-foreground">
            {card.employee_code} • {card.department || "—"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {card.facts.present_days}/{card.facts.applicable_days} days • worked{" "}
          {card.facts.worked_hours}h of {card.facts.required_hours}h • OT {card.facts.ot_hours}h •
          time loss {card.facts.time_loss_hours}h • late {card.facts.late_days} day(s)
        </p>
      </div>
      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
        ZONE_STYLE[card.overall.zone]}`} data-testid={`score-overall-${card.user_id}`}>
        {card.overall.display} • {card.overall.zone_label}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(card.metrics).map(([key, m]) => (
        <ScoreChip key={key} metric={m} testId={`score-${key}-${card.user_id}`} />
      ))}
    </div>
  </div>
);

const BehaviourForm = ({ traits, employees, onSaved }) => {
  const [userId, setUserId] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ratings, setRatings] = useState({});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!userId) return toast.error("Choose an employee");
    if (!Object.keys(ratings).length) return toast.error("Rate at least one trait");
    setBusy(true);
    try {
      await api.post("/performance/behaviour", { user_id: userId, month, ratings });
      toast.success("Behaviour rating saved");
      setRatings({});
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface space-y-3 p-4" data-testid="behaviour-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Employee">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="behaviour-employee"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Month">
          <Input type="month" value={month} data-testid="behaviour-month"
            onChange={(e) => setMonth(e.target.value)} />
        </Field>
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        {traits.map((t) => (
          <Field key={t} label={t.replace(/_/g, " ")}>
            <Select value={String(ratings[t] || "")}
              onValueChange={(v) => setRatings({ ...ratings, [t]: Number(v) })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`behaviour-${t}`}>
                <SelectValue placeholder="1–5" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} ★</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Behaviour is rated by a human manager only — the AI never assigns it.
      </p>
      <Button size="sm" className="gap-1.5" onClick={save} disabled={busy} data-testid="behaviour-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
        Save rating
      </Button>
    </div>
  );
};

const WeightSettings = ({ settings, reload }) => {
  const [form, setForm] = useState(settings);
  const total = Object.values(form.weights).reduce((s, v) => s + Number(v || 0), 0);
  return (
    <div className="surface space-y-3 p-4" data-testid="performance-settings">
      <div className="grid gap-2 sm:grid-cols-4">
        {settings.metrics.map((m) => (
          <Field key={m.key} label={m.label}>
            <Input type="number" data-testid={`weight-${m.key}`} value={form.weights[m.key]}
              onChange={(e) => setForm({ ...form, weights: { ...form.weights, [m.key]: Number(e.target.value) } })} />
          </Field>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        {["green", "yellow", "red"].map((z) => (
          <Field key={z} label={`${z} zone from (%)`}>
            <Input type="number" data-testid={`zone-${z}`} value={form.zones[z]}
              onChange={(e) => setForm({ ...form, zones: { ...form.zones, [z]: Number(e.target.value) } })} />
          </Field>
        ))}
      </div>
      <p className={`text-xs ${total === 100 ? "text-muted-foreground" : "text-destructive"}`}
        data-testid="weight-total">
        Weights total {total} — must be exactly 100. Employees never see these weights.
      </p>
      <Button size="sm" className="gap-1.5" data-testid="performance-settings-save"
        onClick={async () => {
          try {
            await api.put("/performance/settings", { weights: form.weights, zones: form.zones });
            toast.success("Performance settings saved");
            reload();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}>
        <Save className="h-4 w-4" /> Save
      </Button>
    </div>
  );
};

export default function Performance() {
  const [period, setPeriod] = useState("month");
  const [board, setBoard] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = useCallback(() => {
    api.get(`/performance/board?period=${period}`).then(({ data }) => setBoard(data))
      .catch((e) => logError("performance board", e));
    api.get("/performance/settings").then(({ data }) => setSettings(data))
      .catch((e) => logError("performance settings", e));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (!board || !settings) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        testId="performance-header"
        title="Performance Scorecards"
        subtitle={`${board.cards.length} employees • ${board.red_flags.length} in red / danger zone`}
        actions={
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="performance-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["day", "week", "month", "quarter", "year"].map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        <Tabs defaultValue="cards">
          <TabsList data-testid="performance-tabs">
            <TabsTrigger value="cards" data-testid="tab-scorecards">Scorecards</TabsTrigger>
            <TabsTrigger value="ranking" data-testid="tab-ranking">Top 10</TabsTrigger>
            <TabsTrigger value="behaviour" data-testid="tab-behaviour">Behaviour rating</TabsTrigger>
            <TabsTrigger value="weights" data-testid="tab-weights">Weights & zones</TabsTrigger>
          </TabsList>

          <TabsContent value="cards" className="mt-4 space-y-3" data-testid="scorecard-list">
            {board.cards.length === 0 ? <Empty title="No employees yet" />
              : board.cards.map((c) => <Card key={c.user_id} card={c} />)}
          </TabsContent>

          <TabsContent value="ranking" className="mt-4 space-y-3" data-testid="ranking-list">
            {Object.entries(board.rankings).map(([key, r]) => (
              <div key={key} className="surface p-4" data-testid={`ranking-${key}`}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  {key === "overall" ? <Award className="h-4 w-4 text-primary" />
                    : <TrendingUp className="h-4 w-4 text-primary" />}
                  {r.label}
                </div>
                {r.top.length === 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">Not enough data yet.</p>
                ) : (
                  <div className="mt-2 space-y-1">
                    {r.top.map((t) => (
                      <div key={t.user_id} className="flex items-center justify-between border-t border-border py-1.5 text-xs">
                        <span>
                          <b className="font-mono-num">#{t.rank}</b> {t.name}
                        </span>
                        <Badge className={`text-[10px] ${ZONE_STYLE[t.zone]}`} variant="outline">
                          {t.percent}%
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="behaviour" className="mt-4">
            <BehaviourForm traits={settings.traits} employees={board.cards} onSaved={load} />
          </TabsContent>

          <TabsContent value="weights" className="mt-4">
            <WeightSettings settings={settings} reload={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
